import { Customer } from '@/types';

/**
 * 서비스 URL 생성 유틸리티
 */
export const generateServiceUrl = (customerCode: string, alias: string | undefined, mainDomain: string): string => {
  const subdomain = alias || customerCode;
  if (mainDomain === 'localhost') {
    return `http://${subdomain}.localhost`;
  } else {
    return `https://${subdomain}.${mainDomain}`;
  }
};

/**
 * 필드 변경 여부 확인
 */
export const isFieldChanged = <T,>(field: keyof T, editing: T | null, original: T | null): boolean => {
  if (!editing || !original) return false;
  return editing[field] !== original[field];
};

/**
 * 필드 클래스명 생성 (변경된 필드는 노란색으로 표시)
 */
export const getFieldClassName = <T,>(
  field: keyof T | 'plan_id',
  editing: T | null,
  original: T | null,
  isTextarea = false
): string => {
  const baseClass = isTextarea 
    ? "w-full px-3 py-2 border rounded-md" 
    : "w-full px-3 py-2 border rounded-md";
  
  if (field === 'plan_id') {
    // plan_id는 plan 객체와 비교
    if (editing && original && typeof editing === 'object' && typeof original === 'object' && 'plan' in editing && 'plan' in original) {
      const editingPlan = (editing as any).plan;
      const originalPlan = (original as any).plan;
      if (editingPlan?.id !== originalPlan?.id) {
        return `${baseClass} border-yellow-400 bg-yellow-50`;
      }
    }
  } else if (isFieldChanged(field as keyof T, editing, original)) {
    return `${baseClass} border-yellow-400 bg-yellow-50`;
  }
  
  return `${baseClass} border-gray-300`;
};

/**
 * 플랜에 따른 max_users 기본값 계산
 */
export const calculateMaxUsers = (planName: string | undefined, currentMaxUsers: number): number => {
  const plan = planName?.toLowerCase();
  if (plan === 'personal') {
    return 1;
  } else if (plan === 'professional') {
    return currentMaxUsers || 10;
  } else if (plan === 'enterprise') {
    return currentMaxUsers || 30;
  }
  return currentMaxUsers || 10;
};

/**
 * 플랜에 따른 max_users 제한
 */
export const getMaxUsersLimit = (planName: string | undefined): { min: number; max: number | null } => {
  const plan = planName?.toLowerCase();
  if (plan === 'personal') {
    return { min: 1, max: 1 };
  } else if (plan === 'professional') {
    return { min: 1, max: 30 };
  } else if (plan === 'enterprise') {
    return { min: 1, max: null };
  }
  return { min: 1, max: null };
};

/**
 * 고객사 목록 필터링
 */
export const filterCustomers = (customers: Customer[], searchQuery: string): Customer[] => {
  if (!searchQuery.trim()) return customers;
  
  const query = searchQuery.toLowerCase();
  return customers.filter(customer => 
    customer.customer_name.toLowerCase().includes(query) ||
    customer.customer_code.toLowerCase().includes(query) ||
    (customer.alias && customer.alias.toLowerCase().includes(query))
  );
};

